<template>
  <el-dialog v-model="dialogVisible"
             :width="isSmallScreen ? '95%' : undefined"
             :title="t('views.appointments.newAppointment')" @close="onDialogClose">
    <AppointmentForm
        v-if="initAppointment && dialogVisible"
        :key="`${initAppointment.id || 'new'}-${initPet?.id || 'no-pet'}`"
        :initAppointment="initAppointment"
        :initCustomer="initCustomer"
        :initPet="initPet"
        :initVetId="initVetId"
        :showPetField="showPetField"
        :submitAction="submit"
        successMsg=""
        enableClearForm
        clearFormOnSubmit
        className="new-appointment-dialog"
    />
  </el-dialog>

</template>
<script lang="ts" setup>
import {onMounted, ref, watch} from "vue"
import {useI18n} from "vue-i18n";
import {useMediaQuery} from "@vueuse/core";
import {useHTTP} from "@/plugins/http";
import Appointment from "@/models/customer/Appointment";
import AppointmentForm from "./AppointmentForm.vue"
import Customer from "@/models/customer/Customer";
import Pet from "@/models/Pet";

const props = defineProps(['modelValue', 'appointment', 'customer', 'pet', 'showPetField', 'initVetId'])
const emits = defineEmits(['onOpen', 'update:modelValue', 'onSubmit'])
const {t} = useI18n({
  useScope: 'global',
  messages: {
    en: {
    },
    ar: {
    }
  }
});

const http = useHTTP();
const isSmallScreen = useMediaQuery('(max-width: 767px)')
const dialogVisible = ref(false);
const initAppointment = ref(new Appointment());
const initCustomer = ref(new Customer())
const initPet = ref(new Pet())
const initVetId = ref('')

onMounted(() => {
  dialogVisible.value = props.modelValue
  if (props.appointment) {
    initAppointment.value = props.appointment;
    initCustomer.value = (props.appointment as Appointment).customer;
    initPet.value = (props.appointment as Appointment).pet;
  }
  if (props.customer && !props.appointment) {
    initCustomer.value = props.customer
  }
  if (props.pet && !props.appointment) {
    initPet.value = props.pet
  }
});

watch(
    () => props.modelValue,
    (newValue: any, _) => {
      dialogVisible.value = newValue
      // When dialog opens, ensure all data is refreshed from props
      if (newValue) {
        if (props.appointment) {
          initAppointment.value = Appointment.from(props.appointment)
          if (initAppointment.value.pet && initAppointment.value.pet.id) {
            initPet.value = initAppointment.value.pet
          }
          if (initAppointment.value.customer && initAppointment.value.customer.id) {
            initCustomer.value = initAppointment.value.customer
          }
        }
        if (props.customer && !props.appointment) {
          initCustomer.value = props.customer
        }
        if (props.pet && !props.appointment) {
          initPet.value = props.pet
        }
        // Pass vet ID for new appointments
        initVetId.value = props.initVetId || ''
      }
    },
)
watch(
    () => props.appointment,
    (newValue: any, _) => {
      initAppointment.value = Appointment.from(newValue)
      if (initAppointment.value.customer) {
        initCustomer.value = initAppointment.value.customer
      }
      if (initAppointment.value.pet) {
        initPet.value = initAppointment.value.pet
      }
    },
)

watch(() => dialogVisible, (newValue) => {
  emits('update:modelValue', newValue)
})

watch(() => props.customer, (newValue) => {
  initCustomer.value = newValue
}, {deep: true})

watch(() => props.pet, (newValue) => {
  initPet.value = newValue
}, {deep: true})

function onDialogClose() {
  dialogVisible.value = false;
  emits('update:modelValue', dialogVisible.value)
}

async function submit(appointment: Appointment) {
  try {
    if (!props.appointment) {
      const res = await http.post<Appointment>(Appointment.ENDPOINT, appointment)
      emits('onSubmit', res)
    } else {
      const res = await http.put<Appointment>(Appointment.ENDPOINT+`/${appointment.id}`, appointment)
      emits('onSubmit', res)
    }
  } catch (error) {
    return error
  }
}
</script>
<style>
.new-appointment-dialog .input-section {
  width: 100% !important;
}

.new-appointment-dialog  .el-form-item {
  display: block !important;
}
</style>
